import { useQuery } from '@tanstack/react-query';

import type { PickupPoint, PickupPointSearch, ProviderSettings } from '@getjust/gateway';

import { AnalyticsEventName, useTrackAnalytics } from '$src/hooks/client/useTrackAnalytics';
import { useFormMessageAtom } from '$src/hooks/state';
import { gateway } from '$src/http';

export const PICKUP_POINTS_QUERY_KEY = 'QUERY/PICKUP_POINTS_QUERY_KEY';

export function usePickupPoints(provider: ProviderSettings | undefined, search: PickupPointSearch) {
  const { formMessage } = useFormMessageAtom();
  const { track } = useTrackAnalytics();

  return useQuery({
    // shopId is part of the key so a race between formMessage hydration and
    // the first enabled render (e.g. autoSelect/prefetch mounted inline on
    // /authenticated, before formMessage settles) doesn't cache `[]` under
    // shopId=undefined and then serve that empty to every subsequent
    // consumer once shopId finally resolves.
    queryKey: [PICKUP_POINTS_QUERY_KEY, formMessage?.shopId, provider, search],
    enabled:
      Boolean(formMessage?.shopId) &&
      Boolean(provider) &&
      Boolean(search.address1) &&
      Boolean(search.zip) &&
      Boolean(search.city) &&
      Boolean(search.country),
    staleTime: 30000,
    queryFn: async () => {
      try {
        const address1Reshaped =
          search?.address1?.replace(
            /\b(bis|ter|quater|quinquies|sexies|septies|octies|novies|decies|duplex|triplex)\b/gi,
            '',
          ) ?? '';
        const response = await gateway.get<PickupPoint[]>(
          `/api/${formMessage?.shopId}/search-pickup-points?search=${encodeURIComponent(
            JSON.stringify({ ...search, address1: address1Reshaped }),
          )}&provider=${encodeURIComponent(JSON.stringify(provider))}`,
        );

        return response.data;
      } catch {
        track(AnalyticsEventName.PICKUP_POINT_GET_FAILED);
        // Intentionally return empty — pickup point failure is non-blocking, we degrade gracefully
        return [];
      }
    },
  });
}
